<script setup lang="ts">
import { storeToRefs } from 'pinia';
import LoyaltyHubSheet from './Sheet.vue';
import { useLoyaltyHubStore } from '@/stores/loyalty-hub';

// Os prêmios (Rewards) abertos DENTRO de um modal por cima do hub Club
// Fidelidade — em vez de fechar o hub e navegar pra /user/rewards. A ROTA
// CONTINUA existindo e igual: aqui só reaproveitamos a mesma tela que a rota
// usa (`<UserRewards />` legada ou `<UserV2ScreenRewards />`, conforme
// `isUserV2Enabled` — mesma troca de pages/user/rewards.vue), que é
// autossuficiente (busca a lista no `useRewards`, tem tabs, filtros,
// paginação e o próprio modal de resgate).
//
// Estado só em memória (nada na URL), igual roletinha e cashback.
const store = useLoyaltyHubStore();
const { isRewardsOpen } = storeToRefs(store);

// Mesma troca de components/pages/user/rewards.vue: com a flag ligada, o
// hub embute a tela nova (grid 2 colunas) em vez da lista legada.
const { isUserV2Enabled } = useFeatureFlagsJson();

// O conteúdo é montado só depois do slide-up: a lista dispara o fetch no
// onMounted dela, e montar durante a animação faz a subida engasgar. Mesma
// estratégia do hub (que carrega no @after-enter).
const mounted = ref(false);

function close() {
  store.closeRewards();
}

// "Jogar Agora" do modal de resgate leva o jogador pra fora do hub. `close()`
// da store (e não `closeRewards()`) porque o hub e os sheets vivem fora da
// página roteada: fechar só este sheet deixaria o modal do Clube Fidelidade
// por cima do jogo, com o scroll do body ainda travado (ENG-815).
function handlePlayNow() {
  store.close();
}
</script>

<template>
  <LoyaltyHubSheet
    desktop-max-width="720px"
    :open="isRewardsOpen"
    title="Rewards"
    @close="close"
    @closed="mounted = false"
    @opened="mounted = true"
  >
    <!-- `mounted` no v-if e não v-show: a tela de rewards (v2 ou legada)
         precisa MONTAR de novo a cada abertura pro onMounted dela repuxar a
         lista (o jogador pode ter resgatado algo em outra aba/sessão desde a
         última vez). -->
    <div
      v-if="mounted"
      class="lhr-rewards md:h-full"
    >
      <UserV2ScreenRewards
        v-if="isUserV2Enabled"
        embedded
        modal-class="lhr-redeem-modal"
        @play="handlePlayNow"
      />
      <!-- Wrapper próprio só pra legada: o `:deep(> *:first-child)` abaixo
           zera fundo/padding/gap da `UserBox` que a lista antiga embrulha.
           Na v2 esse gap é espaçamento de verdade (tabs/filtros/grid), não
           decoração — por isso ela fica FORA deste wrapper. -->
      <div
        v-else
        class="lhr-rewards-legacy"
      >
        <UserRewards
          modal-class="lhr-redeem-modal"
          @play="handlePlayNow"
        />
      </div>
    </div>

    <!-- Enquanto o painel sobe, o vão fica com o esqueleto da lista em vez de
         vazio. -->
    <div
      v-else
      class="lhr-loading"
    >
      <div
        v-for="i in 6"
        :key="`lhr-skeleton-${i}`"
        class="lhr-skeleton"
      ></div>
    </div>
  </LoyaltyHubSheet>
</template>

<style>
.lhr-redeem-modal {
  z-index: 9997 !important;
}
</style>

<style scoped lang="scss">
// Neutraliza o `UserBox` (`.userPageBox`) que embrulha a lista: na página de
// conta ele é o card — fundo translúcido, blur, cantos e 30px de recuo — sobre o
// fundo da rota. Dentro do painel do modal isso vira caixa dentro de caixa e
// come a largura útil.
// O alvo é o PRIMEIRO filho e não a classe: ela é de CSS module (`.userPageBox`
// vira `userPageBox_<hash>` em build) e o hash muda. O modal de resgate não cai
// aqui — ele é teleportado pro body, então não é filho deste wrapper.
// `!important` porque as regras do módulo têm a mesma especificidade e a ordem
// entre as folhas não é garantida.
.lhr-rewards-legacy :deep(> *:first-child) {
  background: transparent !important;
  backdrop-filter: none !important;
  border-radius: 0 !important;
  padding: 0 !important;
  gap: 0 !important;
}

.lhr-loading {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.lhr-skeleton {
  @apply w-full h-20 rounded-lg bg-texts/5 border border-solid border-texts/10;
}
</style>
